import { useState, useEffect } from "react";
import { t, catName } from "../i18n";
import { categories } from "../data/scenes";
import SceneCard from "./SceneCard";

export default function SceneBrowser({
  lang,
  activeCategory,
  setActiveCategory,
  activeTag,
  setActiveTag,
  searchQuery,
  setSearchQuery,
  searchFocused,
  setSearchFocused,
  filteredScenes,
  onSelectScene,
}) {
  const [trackCounts, setTrackCounts] = useState({});

  useEffect(() => {
    fetch("/api/scenes/track-counts")
      .then((r) => r.json())
      .then((data) => setTrackCounts(data))
      .catch(() => {}); // silently fail, counts stay {}
  }, []);

  return (
    <section id="explore" className="max-w-7xl mx-auto px-6 py-16">
      {/* Active tag chip */}
      {activeTag && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {t(lang, "tags")}:
          </span>
          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
            {activeTag}
          </span>
          <button
            onClick={() => setActiveTag(null)}
            className="text-xs text-gray-400 hover:text-gray-600 transition ml-1"
          >
            ✕
          </button>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        {/* Search */}
        <div
          className={`relative flex-1 transition-all ${
            searchFocused ? "scale-[1.01]" : ""
          }`}
        >
          <svg
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder={t(lang, "searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            className="w-full pl-12 pr-12 py-3 leading-9 bg-white border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 transition shadow-sm text-base"
          />
        </div>

        {/* Category pills */}
        <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 no-scrollbar">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                setActiveCategory(cat.id);
                setActiveTag(null);
              }}
              className={`shrink-0 px-5 py-3 rounded-xl text-sm font-medium transition-all ${
                activeCategory === cat.id
                  ? "bg-indigo-600 text-white shadow-sm shadow-indigo-200"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
              }`}
            >
              {catName(lang, cat.id, cat.name)}
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-500 mb-6">
        {t(lang, "searchResultsFound")}{" "}
        <span className="font-medium text-gray-900">
          {filteredScenes.length}
        </span>{" "}
        {t(lang, "searchResults")}
        {searchQuery && (
          <span className="ml-2">
            {t(lang, "searchAbout")} "
            <span className="text-indigo-600">{searchQuery}</span>
            " {t(lang, "searchResultsFor")}
          </span>
        )}
      </div>

      {/* Grid */}
      {filteredScenes.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredScenes.map((scene) => (
            <SceneCard
              key={scene.id}
              scene={scene}
              onClick={() => onSelectScene(scene)}
              lang={lang}
              trackCount={trackCounts[scene.id]}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            {t(lang, "noResultsTitle")}
          </h3>
          <p className="text-gray-500">
            {t(lang, "noResultsDesc")}
          </p>
        </div>
      )}
    </section>
  );
}
